import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Sun, Moon, X } from 'lucide-react';
import { toast } from 'sonner';

const pad = (x) => String(x).padStart(2, '0');

const addHours = (dateStr, timeStr, hours) => {
  const d = new Date(`${dateStr}T${timeStr || '14:00'}:00`);
  d.setTime(d.getTime() + hours * 3600 * 1000);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
  };
};

const prettyDate = (iso) => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : '';

export const StepDates = ({ config, basics, setBasics, bookedDates, onComplete }) => {
  const today = new Date();
  const [viewDate, setViewDate] = useState(() => {
    const base = basics.check_in_date ? new Date(`${basics.check_in_date}T12:00:00`) : today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const booked = useMemo(() => new Set(bookedDates || []), [bookedDates]);
  const durations = config.durations || [];

  const checkIn = basics.check_in_date;
  const checkOut = basics.check_out_date;
  const singleDay = checkIn && checkOut && checkIn === checkOut;
  const nights = checkIn && checkOut && !singleDay
    ? Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)
    : 0;

  const rangeHasBooked = (startIso, endIso) => {
    const d = new Date(startIso);
    const end = new Date(endIso);
    while (d < end) {
      d.setDate(d.getDate() + 1);
      const iso = d.toISOString().slice(0, 10);
      if (iso !== endIso && booked.has(iso)) return true;
    }
    return false;
  };

  const findDuration = (hrs) => durations.find(d => Number(d.hours) === hrs);

  const pickDay = (iso) => {
    if (!checkIn || (checkIn && checkOut)) {
      setBasics({ ...basics, check_in_date: iso, check_out_date: '', duration_id: '' });
      return;
    }
    if (iso === checkIn) {
      return; // single-day handled via 12/24 chooser below
    }
    if (iso < checkIn) {
      setBasics({ ...basics, check_in_date: iso, check_out_date: '', duration_id: '' });
      return;
    }
    if (rangeHasBooked(checkIn, iso)) {
      toast.error('That stretch includes a booked day — starting fresh from your new date.');
      setBasics({ ...basics, check_in_date: iso, check_out_date: '', duration_id: '' });
      return;
    }
    setBasics({ ...basics, check_out_date: iso, duration_id: 'custom' });
    onComplete && onComplete();
  };

  const pickSingleDay = (hrs) => {
    const dur = findDuration(hrs);
    const inTime = dur?.check_in_time || basics.check_in_time || '14:00';
    const out = addHours(checkIn, inTime, hrs);
    setBasics({
      ...basics,
      check_in_time: inTime,
      check_out_date: out.date,
      check_out_time: out.time,
      duration_id: dur?.id || 'custom'
    });
    onComplete && onComplete();
  };

  const clearDates = () => setBasics({ ...basics, check_in_date: '', check_out_date: '', duration_id: '' });

  // calendar grid
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayISO = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${pad(month + 1)}-${pad(d)}`;
    const isPast = iso < todayISO;
    const isBooked = booked.has(iso);
    const isEdge = iso === checkIn || iso === checkOut;
    const inRange = checkIn && checkOut && iso > checkIn && iso < checkOut;
    cells.push(
      <button
        key={iso}
        type="button"
        disabled={isPast || isBooked}
        onClick={() => pickDay(iso)}
        data-testid={`calc-cal-day-${iso}`}
        className={`aspect-square flex items-center justify-center text-[13px] tabular-nums transition-colors
          ${isPast ? 'text-[#0A1710]/20 cursor-not-allowed' : ''}
          ${isBooked ? 'bg-[#9E4040]/10 text-[#9E4040]/50 line-through cursor-not-allowed' : ''}
          ${isEdge ? 'bg-[#C6A87C] text-[#0A1710] font-bold' : ''}
          ${inRange ? 'bg-[#C6A87C]/25 text-[#0A1710]' : ''}
          ${!isPast && !isBooked && !isEdge && !inRange ? 'hover:bg-[#F3EFEB] text-[#1A3626]' : ''}`}
      >
        {d}
      </button>
    );
  }

  return (
    <div className="space-y-6" data-testid="calc-step-dates">
      <div>
        <p className="font-wvc-display text-2xl md:text-3xl font-light text-[#0A1710] tracking-tight">
          First things first — when&rsquo;s the celebration?
        </p>
        <p className="text-[#1A3626]/60 text-sm font-light mt-2">
          The calendar shows real availability. Tap your check-in date, then your check-out — or keep it to a single magical day.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Calendar */}
        <div className="bg-white border border-[#E5DCCF] p-4">
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-2 hover:bg-[#F3EFEB] transition-colors" data-testid="calc-cal-prev">
              <ChevronLeft className="w-4 h-4 text-[#1A3626]" />
            </button>
            <p className="font-wvc-display text-lg text-[#0A1710]">{viewDate.toLocaleString('en-IN', { month: 'long', year: 'numeric' })}</p>
            <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-2 hover:bg-[#F3EFEB] transition-colors" data-testid="calc-cal-next">
              <ChevronRight className="w-4 h-4 text-[#1A3626]" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="text-[9px] uppercase tracking-wider text-[#1A3626]/40 font-bold py-1">{d}</div>
            ))}
            {cells}
          </div>
          <div className="flex items-center gap-4 mt-3 text-[10px] text-[#1A3626]/50">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#9E4040]/20" /> Booked</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#C6A87C]" /> Your dates</span>
          </div>
        </div>

        {/* Selection panel */}
        <div className="space-y-4">
          {!checkIn && (
            <div className="border border-dashed border-[#C6A87C]/50 bg-[#C6A87C]/5 p-5">
              <p className="text-sm text-[#1A3626]/70 font-light leading-relaxed">
                Every great celebration begins with a date. Pick yours on the calendar &mdash; open days are ready and waiting for you.
              </p>
            </div>
          )}

          {checkIn && !checkOut && (
            <>
              <div className="bg-white border border-[#E5DCCF] p-5" data-testid="calc-dates-partial">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C6A87C]">Check-in</p>
                <p className="font-wvc-display text-xl text-[#0A1710] mt-1">{prettyDate(checkIn)}</p>
                <p className="text-xs text-[#1A3626]/60 font-light mt-2">
                  Lovely choice. Now tap your check-out date on the calendar&hellip;
                </p>
              </div>
              <div className="bg-[#1A3626] p-5" data-testid="calc-single-day-chooser">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C6A87C]">Just need one day?</p>
                <p className="text-xs text-white/60 font-light mt-1.5">Tell us how long the venue should be yours:</p>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => pickSingleDay(12)}
                    className="flex items-center justify-center gap-2 border border-[#C6A87C]/40 text-[#C6A87C] hover:bg-[#C6A87C] hover:text-[#0A1710] py-3 text-[11px] uppercase tracking-[0.15em] font-bold transition-colors"
                    data-testid="calc-single-12h"
                  >
                    <Sun className="w-4 h-4" strokeWidth={1.5} /> 12 hours
                  </button>
                  <button
                    type="button"
                    onClick={() => pickSingleDay(24)}
                    className="flex items-center justify-center gap-2 border border-[#C6A87C]/40 text-[#C6A87C] hover:bg-[#C6A87C] hover:text-[#0A1710] py-3 text-[11px] uppercase tracking-[0.15em] font-bold transition-colors"
                    data-testid="calc-single-24h"
                  >
                    <Moon className="w-4 h-4" strokeWidth={1.5} /> 24 hours
                  </button>
                </div>
                <p className="text-[10px] text-white/40 font-light mt-2.5">A 24-hour booking rolls into the next morning &mdash; we&rsquo;ll set your check-out automatically.</p>
              </div>
            </>
          )}

          {checkIn && checkOut && (
            <div className="bg-white border border-[#C6A87C] p-5" data-testid="calc-dates-confirmed">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C6A87C]">Your dates are open</p>
                  <p className="font-wvc-display text-xl text-[#0A1710] mt-1.5">
                    {prettyDate(checkIn)} <span className="text-[#1A3626]/40">→</span> {prettyDate(checkOut)}
                  </p>
                  <p className="text-xs text-[#1A3626]/60 font-light mt-2" data-testid="calc-dates-compliment">
                    {singleDay || nights === 0
                      ? 'A single-day celebration — short, sweet and unforgettable.'
                      : nights === 1
                        ? 'One beautiful night — the venue is all yours.'
                        : `${nights} nights of celebration — what a wonderful way to do it.`}
                  </p>
                </div>
                <button type="button" onClick={clearDates} className="text-[#1A3626]/40 hover:text-[#9E4040] p-1 transition-colors" title="Clear dates" data-testid="calc-dates-clear">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Fine-tune times */}
          <div className="bg-white border border-[#E5DCCF] p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/50 mb-2.5">Fine-tune your times</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] uppercase tracking-[0.15em] text-[#1A3626]/40 font-bold mb-1">Check-in</label>
                <input
                  type="time"
                  value={basics.check_in_time}
                  onChange={e => setBasics({ ...basics, check_in_time: e.target.value, duration_id: 'custom' })}
                  className="w-full bg-white border border-[#E5DCCF] px-2.5 py-2 text-sm text-[#0A1710] focus:outline-none focus:border-[#C6A87C]"
                  data-testid="calc-checkin-time"
                />
              </div>
              <div>
                <label className="block text-[9px] uppercase tracking-[0.15em] text-[#1A3626]/40 font-bold mb-1">Check-out</label>
                <input
                  type="time"
                  value={basics.check_out_time}
                  onChange={e => setBasics({ ...basics, check_out_time: e.target.value, duration_id: 'custom' })}
                  className="w-full bg-white border border-[#E5DCCF] px-2.5 py-2 text-sm text-[#0A1710] focus:outline-none focus:border-[#C6A87C]"
                  data-testid="calc-checkout-time"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
